import type { Role, Schedule, TeamMember } from './campaigns';
export const stages = [
  'pauta',
  'roteiro',
  'gravado',
  'editado',
  'publicado',
  'medido',
  'descartado',
] as const;
export type Stage = (typeof stages)[number];
export const formats = ['Reel', 'Carrossel', 'Stories', 'YouTube', 'Post'] as const;
export type Format = (typeof formats)[number];
export const stageLabels: Record<Stage, string> = {
  pauta: 'Pauta',
  roteiro: 'Roteiro',
  gravado: 'Gravado',
  editado: 'Editado',
  publicado: 'Publicado',
  medido: 'Medido',
  descartado: 'Descartado',
};
export interface RecordBase {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}
export interface SourceRef {
  path: string;
  hash: string;
  sourceId: string;
  importedAt: string;
  commit: string | null;
}
export interface Campaign extends RecordBase {
  name: string;
  objective: string;
  audience: string;
  cta: string;
  owner: string;
  approver: string;
  startDate: string;
  endDate: string;
  status: 'planejada' | 'ativa' | 'pausada' | 'encerrada';
  notes: string;
  source?: SourceRef;
  strategyReviewRequired?: boolean;
  strategyEditedAt?: string;
  approverMemberId?: string;
}
export interface Content extends RecordBase {
  flgId?: string;
  title: string;
  format: Format;
  stage: Stage;
  owner: string;
  brief: string;
  notes: string;
  campaignIds: string[];
  reserve: boolean;
  source?: SourceRef;
  sourceValues?: { title: string; format: Format; stage: Stage };
  stageEvidence: string;
  sourceMissing?: boolean;
  conflict?: { field: 'stage'; previous: Stage; incoming: Stage; local: Stage };
}
export interface CalendarEntry {
  contentId: string;
  title: string;
  format: string;
  date: string;
  reserve: boolean;
  stage?: Stage;
}
export interface CalendarVersion extends RecordBase {
  kind: 'vigente' | 'proposto' | 'substituido';
  label: string;
  source: SourceRef;
  entries: CalendarEntry[];
}
export interface Proposal extends RecordBase {
  contentId: string;
  baseDate: string;
  targetDate: string;
  baseHash: string | null;
  reason: string;
  status:
    | 'pendente'
    | 'aprovada'
    | 'rejeitada'
    | 'conflito'
    | 'incorporada'
    | 'substituida'
    | 'executada';
  baseScheduleVersion?: number;
  archiveBaseDate?: string;
  decisionNote: string;
}
export interface Publication extends RecordBase {
  contentId: string;
  account: string;
  channel: string;
  date: string;
  url: string;
  evidence: string;
  precision: 'day' | 'second';
  /** `api`: confirmada pelo job de publicação; nesta etapa sempre com `simulated: true`. */
  source: 'manual' | 'meta' | 'api';
  simulated?: boolean;
  publishJobId?: string;
  provenance?: 'archive';
  sourceRef?: SourceRef;
  externalId?: string;
  accountId?: string;
  publishedAt?: string;
  verifiedAt?: string;
}
export interface AuditEvent extends RecordBase {
  action: string;
  entityId: string;
  actor: string;
  before: unknown;
  after: unknown;
}
export interface SourceRevision extends RecordBase {
  path: string;
  hash: string;
  text: string;
  commit: string | null;
}
export interface SyncRun extends RecordBase {
  changed: number;
  sources: number;
  message: string;
  /** Exact source set observed by this run, including absence from a newer import. */
  sourceRefs?: SourceRef[];
}
export interface IntegrationStatus {
  provider: 'meta';
  status: 'not_configured' | 'disconnected' | 'connected' | 'expired' | 'error';
  capabilities: { login: boolean; read: boolean; publish: false };
  message: string;
  missing?: string[];
  apiVersion?: string;
  permissions?: string[];
  expiresAt?: string | null;
  authorization?: import('./meta').MetaAuthorizationHealth;
  lastSyncAt?: string;
  lastError?: string;
  identity?: { id: string; name: string };
  accounts?: SocialAccount[];
}
export interface SocialAccount {
  id: string;
  externalId: string;
  name: string;
  username?: string;
  platform: 'facebook' | 'instagram';
  pageId: string;
  tasks: string[];
  canReadInsights: boolean;
  missingPermissions: string[];
}
export interface MetricResult {
  key: string;
  label: string;
  status: 'available' | 'unavailable' | 'error';
  value: number | null;
  aggregation: 'total' | 'daily_sum' | 'daily_series' | 'provider_value';
  points: { value: number; endTime: string }[];
  description: string;
  error?: string;
  unit?: 'count' | 'milliseconds';
}
export interface SocialMedia {
  platform?: 'instagram' | 'facebook';
  attachmentCount?: number;
  externalId: string;
  accountId: string;
  username: string;
  permalink: string;
  caption: string;
  publishedAt: string;
  mediaType: string;
  productType: 'REELS' | 'FEED' | 'STORY' | 'POST';
}
export interface LinkedMedia extends RecordBase {
  connectionId: string;
  publicationId: string;
  contentId: string;
  campaignId: string | null;
  label?: string;
  campaignEvidence?: { note: string; sourceRef?: SourceRef };
  media: SocialMedia;
  verifiedAt: string;
  sourceRef?: SourceRef;
}
export interface PublicationReview extends RecordBase {
  connectionId: string;
  contentId: string;
  accountIds: string[];
  channel: 'Instagram' | 'Facebook';
  status: 'outside_window' | 'not_located' | 'multiple_posts';
  note: string;
  checkedAt: string;
  sourceRef?: SourceRef;
}
export interface MediaInsightSnapshot extends RecordBase {
  connectionId: string;
  linkedMediaId: string;
  mediaId: string;
  accountId: string;
  kind: 'lifetime_snapshot';
  publishedAt: string;
  collectedAt: string;
  ageSeconds: number;
  apiVersion: string;
  metrics: MetricResult[];
  collectionKey?: string;
  collectionKind?: 'slot' | 'd7';
  collectionDueAt?: string;
}
export interface InsightSnapshot extends RecordBase {
  connectionId: string;
  accountId: string;
  accountName: string;
  platform: 'facebook' | 'instagram';
  since: string;
  until: string;
  timezone: 'UTC';
  apiVersion: string;
  collectedAt: string;
  metrics: MetricResult[];
}
export interface SessionInfo {
  mode: 'local' | 'meta' | 'editorial';
  authenticated: boolean;
  csrf: string;
  user: { id: string; name: string; role: Role } | null;
  integration: IntegrationStatus;
  loginOrigin?: string;
  emailLogin?: { enabled: boolean; available: boolean };
}
export interface State {
  workQueue?: import('./work-queue').WorkQueue;
  currentMemberId: string;
  schedules: Schedule[];
  members: TeamMember[];
  permissions: { edit: boolean; approve: boolean; admin: boolean };
  campaigns: Campaign[];
  contents: Content[];
  calendars: CalendarVersion[];
  proposals: Proposal[];
  publications: Publication[];
  /** Effective campaign attribution for each recorded publication, including Meta overrides. */
  publicationCampaignIds?: Record<string, string[]>;
  audit: AuditEvent[];
  sync: SyncRun | null;
  integration: IntegrationStatus;
  mode: 'local' | 'meta' | 'editorial';
  actor: string;
  csrf: string;
  today: string;
}
